import {
  useFieldArray,
  useWatch,
  Controller,
  type Control,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Twitter,
  Instagram,
  Globe,
  Facebook,
  Linkedin,
  Mail,
} from "lucide-react";
import type { ReactNode } from "react";

const SOCIAL_PLATFORMS = [
  { value: "x", label: "X (Twitter)", icon: <Twitter className="h-4 w-4" /> },
  {
    value: "instagram",
    label: "Instagram",
    icon: <Instagram className="h-4 w-4" />,
  },
  { value: "homepage", label: "Homepage", icon: <Globe className="h-4 w-4" /> },
  {
    value: "facebook",
    label: "Facebook",
    icon: <Facebook className="h-4 w-4" />,
  },
  {
    value: "linkedin",
    label: "LinkedIn",
    icon: <Linkedin className="h-4 w-4" />,
  },
  { value: "mail", label: "Mail", icon: <Mail className="h-4 w-4" /> },
] as const satisfies readonly {
  value: string;
  label: string;
  icon: ReactNode;
}[];

const PLATFORM_ICON_MAP: Record<string, ReactNode> = Object.fromEntries(
  SOCIAL_PLATFORMS.map((p) => [p.value, p.icon]),
);

const LINK_PLACEHOLDER_MAP: Record<string, string> = {
  x: "https://x.com/username",
  instagram: "https://instagram.com/username",
  homepage: "https://example.com",
  facebook: "https://facebook.com/username",
  linkedin: "https://linkedin.com/in/username",
  mail: "email@example.com",
};

interface SocialAccountsFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
}

export function SocialAccountsField({
  control,
  name,
}: SocialAccountsFieldProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const watchedFields = useWatch({ control, name }) as
    | { platformName: string; link: { ko: string; en: string | null } }[]
    | undefined;

  const usedPlatforms = new Set(
    (watchedFields ?? []).map((f) => f.platformName).filter(Boolean),
  );

  const hasAvailablePlatform = usedPlatforms.size < SOCIAL_PLATFORMS.length;

  const getNextAvailablePlatform = () => {
    const available = SOCIAL_PLATFORMS.find((p) => !usedPlatforms.has(p.value));
    return available?.value ?? "";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>소셜 계정 (선택)</Label>
        {hasAvailablePlatform && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append(
                {
                  platformName: getNextAvailablePlatform(),
                  link: { ko: "", en: null },
                },
                { shouldFocus: false },
              )
            }
          >
            <Plus className="mr-1 h-3 w-3" />
            추가
          </Button>
        )}
      </div>

      {fields.map((field, index) => {
        const currentPlatform = watchedFields?.[index]?.platformName ?? "";

        return (
          <div key={field.id} className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <Controller
                name={`${name}.${index}.platformName`}
                control={control}
                render={({ field: platformField }) => (
                  <Select
                    value={platformField.value}
                    onValueChange={platformField.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="플랫폼 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOCIAL_PLATFORMS.map((platform) => {
                        const isUsedByOther =
                          usedPlatforms.has(platform.value) &&
                          platform.value !== currentPlatform;

                        return (
                          <SelectItem
                            key={platform.value}
                            value={platform.value}
                            disabled={isUsedByOther}
                          >
                            <span className="flex items-center gap-2">
                              {platform.icon}
                              {platform.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              />
              <Input
                placeholder={
                  LINK_PLACEHOLDER_MAP[currentPlatform] ?? "링크 URL"
                }
                {...control.register(`${name}.${index}.link.ko`)}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(index)}
              className="mt-1"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      })}

      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">소셜 계정이 없습니다.</p>
      )}
    </div>
  );
}

export { SOCIAL_PLATFORMS, PLATFORM_ICON_MAP };
